import { z } from "zod";
import { getEnv } from "./envManager";
import { AddinRequestAnswerTimeline, type DocumentRequestStep } from "./addin-request-answer-timeline";
import { joinFocusedDocumentRead, type FocusedDocumentSnapshot } from "./focused-document-snapshot";

export interface ContentRequestFulfillmentDeps {
    dioptraFetch: (url: string, options?: RequestInit) => Promise<unknown>;
    sendError: (error: Error, context?: Record<string, unknown>) => void;
}

const contentFulfillmentPayloadSchema = z.discriminatedUnion("success", [
    z.object({
        success: z.literal(false),
        error: z.string(),
        retryable: z.boolean(),
    }),
    z.object({
        success: z.literal(true),
        fileBase64: z.string(),
        fileName: z.string(),
    }),
]);
type ContentFulfillmentPayload = z.infer<typeof contentFulfillmentPayloadSchema>;

function contentRequestFulfillUrl(): string {
    return `${getEnv("WEBAPP_API_BASE_URL")}/api/agent-content-request/fulfill`;
}

export interface JsExecutionFulfillmentPayload {
    stdout: string;
    return_value: string | null;
    error: string | null;
    success: boolean;
}

export async function fulfillJsExecutionRequest(
    requestId: string,
    responsePayload: JsExecutionFulfillmentPayload,
    deps: ContentRequestFulfillmentDeps,
): Promise<void> {
    await deps.dioptraFetch(contentRequestFulfillUrl(), {
        method: "POST",
        body: JSON.stringify({ requestId, responsePayload }),
    });
}

export type DocumentContentRequestOutcome =
    | { kind: "uploaded"; coversEventsThroughSequence: number }
    | { kind: "not_uploaded" };

export function fulfillDocumentContentRequest(
    requestId: string,
    deps: ContentRequestFulfillmentDeps,
): Promise<DocumentContentRequestOutcome> {
    const answerTimeline = new AddinRequestAnswerTimeline<DocumentRequestStep>("document", requestId);
    return answerTimeline.settleWhen(answerDocumentContentRequest(requestId, deps, answerTimeline));
}

async function answerDocumentContentRequest(
    requestId: string,
    deps: ContentRequestFulfillmentDeps,
    answerTimeline: AddinRequestAnswerTimeline<DocumentRequestStep>,
): Promise<DocumentContentRequestOutcome> {
    const fulfillUrl = contentRequestFulfillUrl();
    const postFulfill = (responsePayload: ContentFulfillmentPayload) => {
        answerTimeline.enterStep("fulfill_post");
        return deps.dioptraFetch(fulfillUrl, {
            method: "POST",
            body: JSON.stringify({ requestId, responsePayload: contentFulfillmentPayloadSchema.parse(responsePayload) }),
        });
    };

    const documentRead = joinFocusedDocumentRead({
        component: "contentRequestFulfillment",
        action: "fulfill_addin_content_request",
        sendError: deps.sendError,
    }, answerTimeline.enterStep("document_read"));
    answerTimeline.describeProgressWith(() => ({
        reused_in_flight_read: documentRead.reusedInFlightRead,
        ...documentRead.progress.describe(),
    }));

    let snapshot: FocusedDocumentSnapshot;
    try {
        snapshot = await documentRead.snapshot;
    } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        deps.sendError(error, {
            component: "contentRequestFulfillment",
            action: "fulfill_addin_content_request:document_read",
            requestId,
        });
        await postFulfill({ error: `Reading the document failed: ${error.message}`, success: false, retryable: true });
        return { kind: "not_uploaded" };
    }

    if (snapshot.fileBase64 === null) {
        deps.sendError(new Error("Document too large for agent fulfill"), {
            component: "contentRequestFulfillment",
            action: "fulfill_addin_content_request:size_limit",
            fileName: snapshot.fileName,
        });
        await postFulfill({ error: "Document too large", success: false, retryable: false });
        return { kind: "not_uploaded" };
    }

    await postFulfill({ success: true, fileBase64: snapshot.fileBase64, fileName: snapshot.fileName });
    return { kind: "uploaded", coversEventsThroughSequence: snapshot.coversEventsThroughSequence };
}
